/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jan 12 2024
 **/

import { useEffect } from "react";
import { createPortal } from "react-dom";

import { useChatRoomActions } from "@/frameworks/core/chat";
import { Configure } from "@/frameworks/libraries/utilities";
import { Properties } from "@/frameworks/libraries/properties";
import { useMiniChatPopupManager, useRoomViewActions } from "@/frameworks/core/hooks";
import { useChatDataStore } from "@/modules/chat/store";

import MiniChatPopup, { MiniChatPopupSizeConfig } from "./MiniChatPopup";

function saveMiniChatCoordinate(target: Element, id: string) {
	const { top, left } = target.getBoundingClientRect();
	Configure.miniChatCoordinate = { ...Configure.miniChatCoordinate, [id]: { x: left, y: top } };
}

export default function MiniChatPopupLayer() {
	const showFullSizeChat = useChatDataStore((state) => state.show_fullsize_chat);
	const { useMiniChatListRenderer, miniChatPopupActions } = useMiniChatPopupManager();
	const miniChatListValue = useMiniChatListRenderer();
	const chatRoomActions = useChatRoomActions();
	const { updateRoomView } = useRoomViewActions();

	const openFullSizeChat = () => {
		const currentFocus = miniChatPopupActions.getCurrentFocus();
		if (!currentFocus) return;

		if (currentFocus.type === "DM") {
			chatRoomActions.common.open({ channelID: currentFocus.id, type: "DM", ignoreUpdate: true });
		} else {
			chatRoomActions.common.open({
				channelID: currentFocus.id,
				spaceID: currentFocus.spaceID || "",
				type: "Space",
				ignoreUpdate: true
			});
		}
	};

	useEffect(() => {
		if (!showFullSizeChat) return;

		openFullSizeChat();
		miniChatPopupActions.clear();

		if (miniChatListValue.length > 1) {
			miniChatListValue.forEach((item) => {
				if (item.focus) return;
				else {
					updateRoomView({
						roomId: item.id as string,
						deviceKey: Properties.browserTabID,
						view: false
					});
				}
			});
		}
	}, [showFullSizeChat]);

	return createPortal(
		<>
			{miniChatListValue.map((miniChat) => (
				<MiniChatPopup
					key={miniChat?.id}
					onPointerUp={(ev) =>
						saveMiniChatCoordinate(ev.currentTarget as HTMLDivElement, miniChat.id.slice(0, 10))
					}
					size={
						miniChat?.size ?? {
							width: MiniChatPopupSizeConfig.DEFAULT_WIDTH,
							height: MiniChatPopupSizeConfig.DEFAULT_HEIGHT
						}
					}
					{...miniChat}
				/>
			))}
		</>,
		document.body
	);
}
